// Rendering message delivery outcomes.
//
// Mirrors CampaignRecipient.status in backend/src/models/CampaignRecipient.js and MESSAGE_STATUSES
// in backend/src/models/Contact.js. Shared by the campaign results page and the contacts list,
// because those two screens showing the same failure under different names is how an operator
// stops trusting either of them.

export type MessageStatus = 'pending' | 'queued' | 'sent' | 'delivered' | 'read' | 'failed';

export const MESSAGE_STATUS_LABELS: Record<MessageStatus, string> = {
  pending: 'Pending',
  queued: 'Queued',
  sent: 'Sent',
  delivered: 'Delivered',
  read: 'Read',
  failed: 'Failed',
};

// Mirrors CAPPED_FAILURE_CODE in backend/src/services/campaignPerformanceService.js.
//
// WhatsApp caps how many marketing messages one person receives per day ACROSS ALL BUSINESSES, so
// this is not a fault of the send, the template, or the number. Shown in red beside real failures it
// makes a healthy campaign look broken, which is why it is coloured and worded differently
// everywhere it appears.
export const CAPPED_FAILURE_CODE = '131049';

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

export function messageStatusVariant(status: MessageStatus | null, failureCode?: string | null): BadgeVariant {
  if (!status) return 'outline';
  if (status === 'failed') return failureCode === CAPPED_FAILURE_CODE ? 'secondary' : 'destructive';
  if (status === 'read' || status === 'delivered') return 'default';
  if (status === 'sent') return 'secondary';
  return 'outline';
}

/** Extra tint for the states a variant alone cannot distinguish. */
export function messageStatusClass(status: MessageStatus | null, failureCode?: string | null): string {
  if (status === 'read') return 'bg-blue-500 hover:bg-blue-500/90 text-white border-transparent';
  if (status === 'failed' && failureCode === CAPPED_FAILURE_CODE) {
    return 'bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-100';
  }
  return '';
}

export function messageStatusLabel(status: MessageStatus | null, failureCode?: string | null): string {
  if (!status) return 'Not messaged';
  if (status === 'failed' && failureCode === CAPPED_FAILURE_CODE) return 'Daily cap';
  return MESSAGE_STATUS_LABELS[status] ?? status;
}

export type FailureSeverity = 'info' | 'permanent' | 'config' | 'unknown';

export interface FailureReason {
  label: string;
  hint: string;
  severity: FailureSeverity;
}

// SMS failure labels come from backend/src/services/cequensSmsStatus.js and are copied verbatim
// rather than reworded — the operator may also see them in the provider's own console, and two
// names for one code is worse than a clumsy name.
const SMS_REASONS: Record<string, FailureReason> = {
  sms_2: { label: 'Undelivered', hint: 'The operator could not deliver this message.', severity: 'unknown' },
  sms_5: { label: 'Blocked', hint: 'The operator blocked this message.', severity: 'permanent' },
  sms_16: { label: 'Rejected by SMS Center', hint: 'The operator refused the message.', severity: 'permanent' },
  sms_34: { label: 'Expired', hint: 'The message could not be delivered before it expired — the handset was likely off or out of coverage.', severity: 'unknown' },
};

const WHATSAPP_REASONS: Record<string, FailureReason> = {
  [CAPPED_FAILURE_CODE]: {
    label: 'Daily cap',
    hint: 'WhatsApp limits how many marketing messages one person receives per day, across all businesses. Nothing is wrong with this send — try again tomorrow.',
    severity: 'info',
  },
  '131026': {
    label: 'Not on WhatsApp',
    hint: 'This number has no WhatsApp account. Remove it from the list, or reach this contact by SMS instead.',
    severity: 'permanent',
  },
  '131047': {
    label: 'Outside the 24-hour window',
    hint: 'More than 24 hours have passed since this contact last replied, so only an approved template can be sent.',
    severity: 'info',
  },
  '131051': {
    label: 'Unsupported message type',
    hint: 'The message type is not supported for this recipient.',
    severity: 'config',
  },
  '470': {
    label: 'Failed to send',
    hint: 'The provider rejected the message without giving a specific reason.',
    severity: 'unknown',
  },
};

/**
 * A human explanation of a failure code.
 *
 * The provider's own `errorMessage` is the fallback rather than the primary, because it is written
 * for whoever integrates with the API, not for an estate agent looking at a list of names.
 */
export function failureReason(code?: string | null, errorMessage?: string | null): FailureReason | null {
  if (!code && !errorMessage) return null;

  if (code) {
    const known = WHATSAPP_REASONS[code] ?? SMS_REASONS[code];
    if (known) return known;

    // Meta groups template problems in the 132xxx range. They share one cause and one fix, so they
    // are matched by prefix rather than enumerated — the list changes and a missed code would
    // otherwise render as a bare number.
    if (/^132\d{3}$/.test(code)) {
      return {
        label: 'Template problem',
        hint: errorMessage || 'The template was rejected, paused, or does not match what was sent. Check it in the WhatsApp templates screen.',
        severity: 'config',
      };
    }
  }

  return {
    label: errorMessage || (code ? `Failed (code ${code})` : 'Failed'),
    hint: code ? `Provider code ${code}.` : '',
    severity: 'unknown',
  };
}

/**
 * Is this number worth keeping on the list?
 *
 * Derived here rather than stored as a column on the contact, because the set of permanently-dead
 * codes is Meta's to change: as a function this is a one-line edit, whereas a stored flag would be
 * sticky and silently wrong from the day the list moved.
 */
export function isPermanentFailure(code?: string | null): boolean {
  if (!code) return false;
  return failureReason(code)?.severity === 'permanent';
}

export interface DeliveryStats {
  total: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
  capped: number;
  replied: number;
  optedOut: number;
  pending: number;
  queued: number;
}

// Both rates are measured against what was actually SENT, not against the list size: a message that
// never left is not a delivery failure, and counting it as one makes every campaign with pending
// rows look worse than it is. `delivered` excludes rows that have moved on to `read`, so both are
// added — the ladder is monotonic, so a read message was necessarily delivered.
const reached = (stats: DeliveryStats) => stats.delivered + stats.read;

export function deliveryRate(stats: DeliveryStats): number {
  const attempted = reached(stats) + stats.sent + stats.failed;
  return attempted === 0 ? 0 : Math.round((reached(stats) / attempted) * 100);
}

export function readRate(stats: DeliveryStats): number {
  const delivered = reached(stats);
  return delivered === 0 ? 0 : Math.round((stats.read / delivered) * 100);
}
